import { PermissionGroup } from './types';
import { capitalize } from 'lodash';
import {
  CommonPermission,
  LpPermission,
  RolePermission,
} from 'further-types/admin-user-role';

export const lpPermissionGroups: Array<PermissionGroup> = [
  {
    groupName: 'Minimum requirements',
    groupDescription: 'Includes visibility of vehicles, accounts and actions',
    autoEnabled: true,
    groupItems: [
      {
        permissions: [
          RolePermission.LpReadAccounts,
          RolePermission.LpReadVehicles,
          RolePermission.LpReadActions,
          RolePermission.ReadFirm,
        ],
        label: 'View',
      },
    ],
  },
  {
    groupName: 'Fund setup',
    groupDescription:
      'Includes sensitive data such as your firm URL, bank details and contact details.',
    groupItems: [
      {
        label: 'Manage chart of accounts and account mapping',
        permissions: [
          RolePermission.LpReadAccounts,
          RolePermission.LpCreateAccount,
          RolePermission.LpEditAccount,
          RolePermission.LpDeleteAccount,
          RolePermission.LpCreateTransactionType,
          RolePermission.LpEditTransactionType,
          RolePermission.LpDeleteTransactionType,
        ],
      },
      {
        label: 'Fund setup',
        permissions: [
          RolePermission.LpCreateVehicle,
          RolePermission.LpEditVehicle,
        ],
      },
    ],
  },
  {
    groupName: 'Fund accounting',
    groupDescription:
      'Includes access to the GL, trial balance and action history.',
    groupItems: [
      {
        label: 'View',
        permissions: [RolePermission.ReadGl, RolePermission.LpReadActions],
      },
    ],
  },

  {
    groupName: 'Incomes and expenses',
    groupDescription: 'Creation and management of incomes and expenses.',
    groupItems: [
      {
        id: 'incomes-and-expenses-view',
        label: 'View',
        permissions: [
          RolePermission.LpReadIncomeAndExpenses,
          RolePermission.LpReadContacts,
        ],
      },
      {
        label: 'Create and update',
        requiresId: 'incomes-and-expenses-view',
        permissions: [
          RolePermission.LpCreateIncomeAndExpenses,
          RolePermission.LpEditIncomeAndExpenses,
        ],
      },
      {
        label: 'Delete',
        requiresId: 'incomes-and-expenses-view',
        permissions: [RolePermission.LpDeleteIncomeAndExpenses],
      },
    ],
  },
  {
    groupName: 'Bank reconciliation',
    groupDescription: 'Ability to reconcile actions to bank statement lines.',
    groupItems: [
      {
        id: 'bank-reconciliation-view',
        label: 'View',
        permissions: [
          RolePermission.LpReadBankFeed,
          RolePermission.LpReadBankAccount,
        ],
      },
      {
        label: 'Reconcile',
        requiresId: 'bank-reconciliation-view',
        permissions: [
          RolePermission.LpCreateBankAccountOperation,
          RolePermission.LpEditBankAccountOperation,
        ],
      },
      {
        label: 'Delete statement lines',
        requiresId: 'bank-reconciliation-view',
        permissions: [RolePermission.LpDeleteBankAccountOperation],
      },
      {
        label: 'Add/deactivate bank account',
        requiresId: 'bank-reconciliation-view',
        permissions: [
          RolePermission.LpCreateBankAccount,
          RolePermission.LpEditBankAccount,
        ],
      },
    ],
  },
  {
    groupName: 'Investments',
    groupDescription: 'Creation and management of investments.',
    groupItems: [
      {
        id: 'investments-view',
        label: 'View',
        permissions: [RolePermission.LpReadInvestment],
      },
      {
        label: 'Create and update',
        requiresId: 'investments-view',
        permissions: [
          RolePermission.LpCreateInvestment,
          RolePermission.LpReadAssets,
          RolePermission.LpReadHoldingClasses,
          RolePermission.LpWriteAssets,
        ],
      },
      {
        label: 'Delete',
        requiresId: 'investments-view',
        permissions: [RolePermission.LpDeleteInvestment],
      },
    ],
  },
  {
    groupName: 'Onboarding',
    groupDescription:
      'Manage investor onboarding, document signing, KYC and onboarding settings.',
    groupItems: [
      {
        id: 'onboarding-view',
        label: 'View',
        permissions: [
          RolePermission.LpReadCommitment,
          RolePermission.LpReadVehicles,
          RolePermission.ReadUsers,
          RolePermission.ReadFirm,
          RolePermission.UseImpersonationAccessInvestor,
        ],
      },
      {
        label: 'Manage',
        requiresId: 'onboarding-view',
        description:
          'Includes the ability to manage application steps, document signing, KYC and onboarding settings.',
        permissions: [
          RolePermission.LpEditCommitment,
          RolePermission.LpEditVehicle,
        ],
      },
    ],
  },
  {
    groupName: 'Realisations',
    groupDescription: 'Creation and management of realisations.',
    groupItems: [
      {
        id: 'realisations-view',
        label: 'View',
        permissions: [
          RolePermission.LpReadRealisation,
          RolePermission.LpReadAssets,
          RolePermission.LpReadHoldingClasses,
        ],
      },
      {
        label: 'Create',
        requiresId: 'realisations-view',
        permissions: [RolePermission.LpCreateRealisation],
      },
      {
        label: 'Delete',
        requiresId: 'realisations-view',
        permissions: [RolePermission.LpDeleteRealisation],
      },
    ],
  },
  {
    groupName: 'Capital actions',
    groupDescription: 'Creation and management of capital actions and notices.',
    groupItems: [
      {
        id: 'capital-actions-view',
        label: 'View',
        permissions: [
          RolePermission.LpReadActions,
          RolePermission.LpReadCapitalAction,
          RolePermission.LpReadCapitalActionNotice,
        ],
      },
      {
        label: 'Manage capital actions',
        requiresId: 'capital-actions-view',
        permissions: [
          RolePermission.LpCreateCapitalAction,
          RolePermission.LpEditCapitalAction,
          RolePermission.LpDeleteCapitalAction,
        ],
      },
      {
        label: 'Manage capital action notices',
        requiresId: 'capital-actions-view',
        description:
          'Includes the ability to send and delete capital action notices.',
        permissions: [
          RolePermission.LpReadCapitalActionNotice,
          RolePermission.LpEditCapitalActionNotice,
          RolePermission.LpDeleteCapitalActionNotice,
        ],
      },
    ],
  },
  {
    groupName: 'Equalisations',
    groupDescription: 'Creation and management of equalisations.',
    groupItems: [
      {
        label: 'Manage',
        permissions: [
          RolePermission.LpReadEqualisations,
          RolePermission.LpCreateEqualisation,
        ],
      },
    ],
  },
  {
    groupName: 'Management fees',
    groupDescription: 'Creation and management of management fees.',
    groupItems: [
      {
        id: 'management-fees-view',
        label: 'View',
        permissions: [
          RolePermission.LpReadFee,
          RolePermission.LpReadFeeTemplate,
        ],
      },
      {
        label: 'Manage',
        requiresId: 'management-fees-view',
        permissions: [RolePermission.LpEditFee],
      },
    ],
  },
  {
    groupName: 'Commitments',
    groupDescription: 'Viewing commitment data and investment performance.',
    groupItems: [
      {
        id: 'commitments-view',
        label: 'View',
        permissions: [RolePermission.LpReadCommitment],
      },
      {
        label: 'Manage',
        description: 'Includes the ability to edit and delete commitments.',
        requiresId: 'commitments-view',
        permissions: [
          RolePermission.LpEditCommitment,
          RolePermission.LpDeleteCommitment,
        ],
      },
    ],
  },
  {
    groupName: 'Accounting calendar',
    groupDescription: 'Manage the accounting calendar.',
    groupItems: [
      {
        label: 'View and manage prepayments and accruals',
        permissions: [
          RolePermission.LpReadPrepayments,
          RolePermission.LpReadAccruals,
          RolePermission.LpCreatePrepayments,
        ],
      },
      {
        id: 'unrealised-fx-gains-losses-view',
        label: 'View unrealised FX gains/losses',
        permissions: [
          RolePermission.LpReadUnrealisedFxGainOrLoss,
          RolePermission.LpReadUnrealisedCashFxGainOrLoss,
          RolePermission.LpReadUnrealisedAssetFxGainOrLoss,
        ],
      },
      {
        label: 'Post and delete unrealised FX gains/losses',
        requiresId: 'unrealised-fx-gains-losses-view',
        permissions: [
          RolePermission.LpCreateUnrealisedFxGainOrLoss,
          RolePermission.LpCreateUnrealisedCashFxGainOrLoss,
          RolePermission.LpCreateUnrealisedAssetFxGainOrLoss,
          RolePermission.LpDeleteUnrealisedFxGainOrLoss,
          RolePermission.LpDeleteUnrealisedCashFxGainOrLoss,
          RolePermission.LpDeleteUnrealisedAssetFxGainOrLoss,
        ],
      },
    ],
  },
  {
    groupName: 'Assets and revaluations',
    groupDescription: 'Manage the assets.',
    groupItems: [
      {
        label: 'View',
        permissions: [
          RolePermission.LpReadAssets,
          RolePermission.LpReadHoldingClasses,
        ],
      },
      {
        label: 'Manage',
        permissions: [
          RolePermission.LpWriteAssets,
          RolePermission.LpWriteHoldingClasses,
        ],
      },
    ],
  },
  {
    groupName: 'Firm and users',
    groupDescription: 'Manage the firm and its users.',
    groupItems: [
      {
        label: 'Edit firm settings',
        permissions: [RolePermission.EditFirm],
      },
      {
        id: 'users-view',
        label: 'View users',
        permissions: [RolePermission.ReadUsers],
      },
      {
        label: 'Add, edit and remove users from your firm',
        requiresId: 'users-view',
        id: 'users-manage',
        permissions: [
          RolePermission.EditUsers,
          RolePermission.ReadRole,
          RolePermission.AddUserToFirm,
          RolePermission.RemoveUserFromFirm,
        ],
      },
      {
        label: 'Manage custom user roles',
        requiresId: 'users-manage',
        description:
          'Includes the ability to create, edit and remove custom user roles.',
        permissions: [
          RolePermission.CreateRole,
          RolePermission.UpdateRole,
          RolePermission.DeleteRole,
        ],
      },
    ],
  },
  {
    groupName: 'Investors',
    groupDescription: 'Viewing investor data and performance.',
    groupItems: [
      {
        id: 'investors-view',
        label: 'View',
        permissions: [RolePermission.ReadInvestor],
      },
      {
        label: 'Manage investors',
        requiresId: 'investors-view',
        description:
          'Includes the ability to create, edit and remove investors.',
        permissions: [
          RolePermission.CreateInvestor,
          RolePermission.EditInvestor,
          RolePermission.DeleteInvestor,
        ],
      },
    ],
  },
  {
    groupName: 'Partner transfers',
    groupDescription: 'Creation and management of partner transfers.',
    groupItems: [
      {
        label: 'Create a partner transfer',
        permissions: [RolePermission.LpCreatePartnerTransfer],
      },
    ],
  },
];

const lpRolePermissionsNotIncludedInGroups = [
  ...Object.values(LpPermission),
  ...Object.values(CommonPermission),
].filter(
  (permission) =>
    !lpPermissionGroups.some((group) =>
      group.groupItems.some((item) => item.permissions.includes(permission)),
    ),
);

export const lpNonGroupedPermissions: PermissionGroup = {
  groupName: 'Other permissions',
  groupDescription: 'Permissions that are not grouped into a specific category',
  groupItems: lpRolePermissionsNotIncludedInGroups.map((permission) => ({
    permissions: [permission],
    label: capitalize(permission.replace(':', ' ').replace('lp ', 'LP ')),
  })),
};
